<script setup lang="ts">
    import { inject } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { RenewalExclusionReason, SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes } from '@shared/components/BsAlerts';
    import { formatMoney } from '@cwd/Shared/utils/formatMoney';
    import { formatNumber } from '@cwd/Shared/utils/formatNumber';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    function getStatusClass(conditionMet: boolean): string {
        return conditionMet ? 'status-not-ok' : 'status-ok';
    }
</script>

<template>
    <Loading
        :show="subscriptionAccess.state.loadingMonthlyPriceBreakdown || subscriptionAccess.state.loadingRenewalExclusionReasons"
        loadingStyle="progress-bar"
        :fixed="true" />
    <div v-if="!subscriptionAccess.state.loadingMonthlyPriceBreakdown && !subscriptionAccess.state.loadingRenewalExclusionReasons">
        <div class="m-lg-b">
            <h4>Monthly price breakdown</h4>
            <template
                v-if="
                    subscriptionAccess.state.monthlyPriceBreakdown &&
                    subscriptionAccess.state.boServiceSubscription &&
                    subscriptionAccess.state.monthlyPriceWithAddons
                ">
                <ul class="list-unstyled">
                    <li class="m-xs-b">
                        <strong>Base price:</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceBreakdown.BasePrice,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>
                            Extra accounts price ({{
                                formatMoney(
                                    subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.PerAccountPrice,
                                    subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                                )
                            }}):
                        </strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceBreakdown.ExtraAccountsPrice,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>Monthly discount ({{ subscriptionAccess.state.boServiceSubscription.MonthlyDiscount }}%):</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceBreakdown.MonthlyDiscount,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>Account discount ({{ subscriptionAccess.state.boServiceSubscription.PerAccountDiscount }}%):</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceBreakdown.AccountDiscount,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>Percentual discount ({{ subscriptionAccess.state.boServiceSubscription.MonthlyDiscountPercentage }}%):</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceBreakdown.PercentualDiscount,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>Total addons price:</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceWithAddons - subscriptionAccess.state.monthlyPriceBreakdown.TotalPrice,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>Total price (excl. addons):</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceBreakdown.TotalPrice,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                    <li class="m-xs-b">
                        <strong>Total price (incl. addons):</strong>
                        {{
                            formatMoney(
                                subscriptionAccess.state.monthlyPriceWithAddons,
                                subscriptionAccess.state.boServiceSubscription.ServiceLevelPrice.Currency.CurrencyCode
                            )
                        }}
                    </li>
                </ul>

                <BsAlert
                    class="m-lg-t"
                    :type="AlertTypes.Info"
                    :dismissible="false"
                    content="The prices above are for right now. The price for the subscription might be different next month." />
            </template>
        </div>

        <div class="m-lg-b">
            <h4>Renewal exclusion reasons</h4>
            <template v-if="subscriptionAccess.state.renewalExclusionReason">
                <ul class="list-unstyled">
                    <li class="m-xs-b">
                        <i
                            :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.SubscriptionEnded)"
                            class="fa fa-square"
                            aria-hidden="true"></i>
                        {{ subscriptionAccess.state.renewalExclusionReason.SubscriptionEnded ? 'Subscription has ended' : 'Subscription has not ended' }}
                    </li>
                    <li class="m-xs-b">
                        <i
                            :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.BillingStartNotSet)"
                            class="fa fa-square"
                            aria-hidden="true"></i>
                        {{ subscriptionAccess.state.renewalExclusionReason.BillingStartNotSet ? 'Billing start not set' : 'Billing start is set' }}
                    </li>
                    <li class="m-xs-b">
                        <i
                            :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.BillingStartInFuture)"
                            class="fa fa-square"
                            aria-hidden="true"></i>
                        {{
                            subscriptionAccess.state.renewalExclusionReason.BillingStartInFuture
                                ? 'Billing starts in the future'
                                : 'Billing does not start in the future'
                        }}
                    </li>
                    <li class="m-xs-b">
                        <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.BillingEnded)" class="fa fa-square" aria-hidden="true"></i>
                        {{ subscriptionAccess.state.renewalExclusionReason.BillingEnded ? 'Billing has ended' : 'Billing has not ended' }}
                    </li>
                    <li class="m-xs-b">
                        <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.AlreadyPaid)" class="fa fa-square" aria-hidden="true"></i>
                        {{ subscriptionAccess.state.renewalExclusionReason.AlreadyPaid ? 'Already paid' : 'Not already paid' }}
                    </li>
                    <li class="m-xs-b">
                        <i :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.AlreadyRenewed)" class="fa fa-square" aria-hidden="true"></i>
                        {{ subscriptionAccess.state.renewalExclusionReason.AlreadyRenewed ? 'Already renewed' : 'Not already renewed' }}
                    </li>
                    <li class="m-xs-b">
                        <i
                            :class="getStatusClass(subscriptionAccess.state.renewalExclusionReason.CreditCardPaymentMethod)"
                            class="fa fa-square"
                            aria-hidden="true"></i>
                        {{
                            subscriptionAccess.state.renewalExclusionReason.CreditCardPaymentMethod
                                ? 'Credit card payment method'
                                : 'Not credit card payment method'
                        }}
                    </li>
                </ul>
            </template>

            <BsAlert
                class="m-lg-t"
                :type="AlertTypes.Info"
                :dismissible="false"
                content="If one of the above is red, the subscription will not show up for renewal this month in billing." />
        </div>
    </div>
</template>

<style scoped></style>
